/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

export const GODAVARI_ATTRIBUTE_VALUES = {
  app_subscribe_type: ['subscribed', 'registered', 'anonymous'],
  video_streaming_mode: ['streaming', 'downloaded'],
  video_video_type: ['vod', 'live'],
  video_content_format: ['HLS', 'DASH', 'UNKNOWN'],
  video_drm: ['clear', 'fairplay', 'widevine', 'playready', 'clearkey'],
  video_content_prefetch: ['yes', 'no', 'adc_ndrm_1', 'adc_ndrm_2', 'adc_drm_1', 'adc_drm_2', 'adc_drm_3', 'adc_drm_4'],
  video_18plus: ['yes', 'no'],
  event_ftl: ['true', 'false'],
  ads_position: ['pre-roll', 'mid-roll', 'post-roll'],
  vmap_success: ['true', 'false'],
} as const;

type GodavariAttribute<K extends keyof typeof GODAVARI_ATTRIBUTE_VALUES> =
  (typeof GODAVARI_ATTRIBUTE_VALUES)[K][number];
export type GodavariDrm = GodavariAttribute<'video_drm'>;
export type GodavariSubscription = GodavariAttribute<'app_subscribe_type'>;
export type GodavariContentId = number | `${bigint}`;

export interface GodavariMetadataStrings {
  app_user_id?: string;
  app_device_id?: string;
  app_subscribe_type?: GodavariSubscription;
  app_partner_id?: string;
  app_version?: string;
  app_build_version?: string;
  app_platform_type?: string;
  app_operating_system?: string;
  app_hardware?: string;
  app_geo_location?: string;
  app_profile_id?: string;
  app_gv_device_id?: string;
  app_gv_session_id?: string;
  app_ppid?: string;
  app_sliv_profile_id?: string;
  video_session_id?: string;
  video_content_id?: GodavariContentId;
  video_streaming_mode?: GodavariAttribute<'video_streaming_mode'>;
  video_video_type?: GodavariAttribute<'video_video_type'>;
  video_player_name?: string;
  video_player_version?: string;
  video_content_format?: GodavariAttribute<'video_content_format'>;
  video_drm?: GodavariDrm;
  video_codec?: string;
  selected_audio_codec?: string;
  selected_video_codec?: string;
  video_cdn?: string;
  video_network_type?: string;
  video_connection_speed?: string;
  video_session_classifier_experiment_id?: string;
  video_connect_time?: string;
  video_binge?: string;
  video_entry_point?: string;
  subscribed_pack_name?: string;
  subscribed_pack_resolution?: string;
  max_entitled_video_resolution?: string;
  initial_video_setting?: string;
  updated_video_setting?: string;
  video_resolution?: string;
  video_starting_bitrate?: string;
  video_audio_language?: string;
  video_subtitle_language?: string;
  video_streaming_host?: string;
  video_delivery_protocol?: string;
  video_playing_rate?: string;
  video_time_to_first_byte?: string;
  video_content_prefetch?: GodavariAttribute<'video_content_prefetch'>;
  video_18plus?: GodavariAttribute<'video_18plus'>;
  event_ftl?: GodavariAttribute<'event_ftl'>;
  event_error_code?: string;
  event_error_message?: string;
  event_exception?: string;
  ads_session_id?: string;
  ads_asset_name?: string;
  ads_creative_id?: string;
  ads_system?: string;
  ads_description?: string;
  ads_advertiser?: string;
  ads_stream_url?: string;
  ads_campaign_id?: string;
  ads_advertiser_id?: string;
  ads_campaign_name?: string;
  ads_technology?: string;
  ads_stitcher?: string;
  ads_is_slate?: string;
  ads_media_file_api_framework?: string;
  ads_manager_name?: string;
  ads_manager_version?: string;
  ads_first_ad_system?: string;
  ads_first_ad_id?: string;
  ads_first_creative_id?: string;
  ads_position?: GodavariAttribute<'ads_position'>;
  adend_event_initiator?: string;
  vmap_prefetch?: string;
  vmap_success?: GodavariAttribute<'vmap_success'>;
}

export type GodavariNumericFields = {
  app_user_age: number;
  normalized_watch_time: number;
  video_length: number;
  video_position: number;
  video_sequence: number;
  video_session_start_time: number;
  video_current_bitrate: number;
  video_session_watch_time_duration: number;
  video_increment_buffer_time_duration: number;
  video_monetizable_video_views: number;
  estimated_bandwidth_in_kbbs: number;
  buffer_available_in_milliseconds: number;
  is_multicam_asset: number;
  video_parent_id: number;
  video_dvr: number;
  sponsor_content: number;
  event_current_playback_rate: number;
  ads_current_ad_count: number;
  ads_configured_ad_count: number;
  ads_pod_position: number;
  ads_length_watched: number;
  ads_length_duration: number;
  ads_current_bitrate: number;
  ads_pod_ad_position: number;
  ads_pod_max_ad_count: number;
  ads_previous_pod: number;
  ads_ad_buffer_duration: number;
  ads_watch_time_duration: number;
  ads_prefetch: number;
  ad_pod_play_position: number;
  ads_timestamp_of_adPlay: number;
};

export type GodavariMetadataInput = GodavariMetadataStrings & {
  [K in keyof GodavariNumericFields]?: number | string;
} & {
  video_dropped_frames?: number;
  custom_tags?: Record<string, unknown>;
};

export type GodavariMetadata = Omit<GodavariMetadataInput, keyof GodavariNumericFields> &
  Partial<GodavariNumericFields>;

export type GodavariApplicationMetadata = Pick<
  GodavariMetadata,
  Extract<keyof GodavariMetadata, `app_${string}`> | 'custom_tags'
>;
export type GodavariApplicationMetadataInput = Pick<GodavariMetadataInput, keyof GodavariApplicationMetadata>;
